import { HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { HAND_CONNECTIONS } from './constants';
import { drawConnectors, drawLandmarks } from './drawing.utils';
import { Particle } from './particles';

export class HandTracker {
  constructor() {
    this.handLandmarker = null;
    this.runningMode = "VIDEO";
    this.lastVideoTime = -1;
    this.results = undefined;
    this.visualStyle = 'default';
    this.particles = [];
    this.lastPinchStates = new Map();
  }

  async initialize() {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
    );

    this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: `https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task`,
        delegate: "GPU"
      },
      runningMode: this.runningMode,
      numHands: 2
    });

    return this;
  }

  async detectFrame(video, canvas, timestamp) {
    if (!this.handLandmarker) return;

    const ctx = canvas.getContext('2d');
    
    if (this.lastVideoTime !== video.currentTime) {
      this.lastVideoTime = video.currentTime;
      this.results = this.handLandmarker.detectForVideo(video, timestamp);
    }

    this.drawResults(ctx, canvas);
  }

  drawResults(ctx, canvas) {
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Update and draw particles
    this.particles = this.particles.filter(particle => particle.life > 0);
    for (const particle of this.particles) {
      particle.update();
      particle.draw(ctx);
    }
    
    const styles = {
      default: {
        connectors: [
          { gradient: ["#4f46e5", "#818cf8"], lineWidth: 8 },
          { gradient: ["#7c3aed", "#c084fc"], lineWidth: 8 }
        ],
        landmarks: { color: "rgba(255, 255, 255, 0.9)", lineWidth: 2 },
        background: null
      },
      neon: {
        connectors: { color: "#22ff88", lineWidth: 3 },
        landmarks: { color: "#00ffff", lineWidth: 2 },
        background: "rgba(0, 0, 0, 0.8)"
      },
      minimal: {
        connectors: { color: "#ffffff", lineWidth: 2 },
        landmarks: { color: "#ffffff", lineWidth: 1 },
        background: null
      }
    };

    if (this.results?.landmarks) {
      const style = styles[this.visualStyle] || styles.default;
      
      // Apply background overlay for neon style
      if (style.background) {
        ctx.fillStyle = style.background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Set glow effect
      ctx.shadowBlur = this.visualStyle === 'neon' ? 20 : 0;
      ctx.shadowColor = style.connectors.color;
      
      // Draw hands with different gradients
      this.results.landmarks.forEach((landmarks, index) => {
        if (this.visualStyle === 'default') {
          const gradientStyle = style.connectors[index % 2];
          const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          gradient.addColorStop(0, gradientStyle.gradient[0]);
          gradient.addColorStop(1, gradientStyle.gradient[1]);
          
          drawConnectors(ctx, landmarks, HAND_CONNECTIONS, {
            color: gradient,
            lineWidth: gradientStyle.lineWidth
          });
          
          // Draw white outlined circles for landmarks
          for (const landmark of landmarks) {
            const x = landmark.x * canvas.width;
            const y = landmark.y * canvas.height;
            
            // White outline
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, 2 * Math.PI);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.lineWidth = 2;
            ctx.stroke();
            
            // Gradient fill
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, 2 * Math.PI);
            ctx.fillStyle = gradientStyle.gradient[1];
            ctx.fill();
          }
        } else {
          drawConnectors(ctx, landmarks, HAND_CONNECTIONS, style.connectors);
          drawLandmarks(ctx, landmarks, style.landmarks);
        }
        
        if (this.visualStyle === 'neon') {
          // Enhanced neon landmarks
          for (const landmark of landmarks) {
            ctx.beginPath();
            ctx.arc(
              landmark.x * canvas.width,
              landmark.y * canvas.height,
              8,
              0,
              2 * Math.PI
            );
            ctx.fillStyle = style.landmarks.color;
            ctx.fill();
            
            // Add extra glow
            ctx.beginPath();
            ctx.arc(
              landmark.x * canvas.width,
              landmark.y * canvas.height,
              4,
              0,
              2 * Math.PI
            );
            ctx.fillStyle = '#ffffff';
            ctx.fill();
          }
        } else {
          drawLandmarks(ctx, landmarks, style.landmarks);
        }

        // Only check for pinch in minimal style
        if (this.visualStyle === 'minimal') {
          this.checkPinchGesture(landmarks, canvas, style.connectors.color);
        }
      });
    }

    ctx.restore();
  }

  checkPinchGesture(landmarks, canvas, color) {
    // Get thumb tip and index finger tip positions
    const thumbTip = landmarks[4];
    const fingerTips = [
      landmarks[8],  // index
      landmarks[12], // middle
      landmarks[16], // ring
      landmarks[20]  // pinky
    ];
    
    // Find the closest finger to thumb
    let minDistance = Infinity;
    let closestFingerTip = null;
    
    for (const fingerTip of fingerTips) {
      const distance = Math.hypot(
        (thumbTip.x - fingerTip.x) * canvas.width,
        (thumbTip.y - fingerTip.y) * canvas.height
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestFingerTip = fingerTip;
      }
    }
    
    // Get hand ID (using position as simple identifier)
    const handId = `${Math.round(landmarks[0].x * 100)}-${Math.round(landmarks[0].y * 100)}`;
    const lastPinchState = this.lastPinchStates.get(handId) || false;
    
    // Check if pinching (fingers are close)
    const isPinching = minDistance < 80; // Even more forgiving pinch distance
    
    // Create particles when fingers are near
    if (minDistance < 120) { // Larger distance for particle creation
      // Create particles at the pinch location
      const x = (thumbTip.x + closestFingerTip.x) * canvas.width / 2;
      const y = (thumbTip.y + closestFingerTip.y) * canvas.height / 2;
      
      // Create fewer particles but continuously
      const particleCount = Math.floor(Math.random() * 3) + 1;
      for (let i = 0; i < particleCount; i++) {
        this.particles.push(new Particle(x, y, 'random'));
      }
    }
    
    // Update pinch state
    this.lastPinchStates.set(handId, isPinching);
  }

  setVisualStyle(style) {
    this.visualStyle = style;
    
    // Update info panel
    const infoPanel = document.querySelector('.info-panel');
    if (infoPanel) {
      switch (style) {
        case 'default':
          infoPanel.textContent = 'Classic Track: Real-time hand tracking with smooth visualization';
          break;
        case 'neon':
          infoPanel.textContent = 'Neon Pulse: Experience your hand movements with a cyberpunk aesthetic';
          break;
        case 'minimal':
          infoPanel.textContent = 'Particle Magic: Pinch your fingers together to create colorful particle effects';
          break;
      }
      infoPanel.classList.add('visible');
      setTimeout(() => infoPanel.classList.remove('visible'), 3000);
    }
  }
}